// NPM MODULES
import React, { useEffect, useState, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
} from '@mui/material';

// FORMATTED MESSAGES
import InvoiceShowFormattedMessages from '../../../../messages/billing/InvoiceShowFormattedMessages';

// IMGS
import roxLogo from '../../../../images/Logo-RO4.gif';

// NETWORKING
import useInvoiceRequest from '../../../../requests/InvoiceRequest';

// UTILS
import { formatCurrency, getCurrencySymbol } from '../../../../utils/currency';
import {
  getCurrencyType,
  getBillDates,
} from '../../../../utils/invoicePresenter';

const SingleInvoice = (props) => {
  /*
    EXPECTED PROPS
      - invoice: Bill object
  */

  const [invoice, setInvoice] = useState(null);
  const { getSingleInvoice } = useInvoiceRequest();

  const { customer, tab } = useParams();

  useEffect(() => {
    async function getData() {
      if (props?.invoice) {
        setInvoice(props.invoice);
        return;
      }

      if (customer && tab) {
        const singleInvoiceData = await getSingleInvoice(
          customer,
          tab,
          props.errorHandler
        );
        setInvoice(singleInvoiceData);
      }
    }

    getData();
  }, [props, customer, tab]);

  /*
    Generates an Invoice Header
  */
  const generateInvoiceHeader = () => {
    let billerAddress = '';

    if (invoice) billerAddress = invoice.billerAddress;

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 4,
        }}
      >
        <Box>
          <img src={roxLogo} alt="Red Oxygen Logo" />
        </Box>
        <Box sx={{ width: 300 }}>
          <Typography
            component="h2"
            sx={{ color: 'common.black', fontSize: 20, fontWeight: 700 }}
          >
            {InvoiceShowFormattedMessages.taxInvoice}
          </Typography>
          <Box dangerouslySetInnerHTML={{ __html: billerAddress }}></Box>
        </Box>
      </Box>
    );
  };

  const generateCustomerHeader = () => {
    let attentionTo = 'Admin';
    let customerName = '';

    if (invoice?.address_to?.customer_name) {
      attentionTo = invoice.address_to.customer_name;
      customerName = invoice.address_to.customer_name;
    }
    if (invoice?.address_to?.contact_name) {
      attentionTo = invoice.address_to.contact_name;
    }

    return (
      <Box>
        <Typography>
          {InvoiceShowFormattedMessages.attention}: {attentionTo}
        </Typography>
        <Typography>
          <strong>{customerName}</strong>
        </Typography>
      </Box>
    );
  };

  /*
    Generate the Dates for the Invoice
  */
  const generateInvoiceDates = () => {
    return (
      <Box>
        <Typography>
          {InvoiceShowFormattedMessages.invoicedDate}:{' '}
          {invoice?.bill_dates ? invoice?.bill_dates?.bill_date : ''}
        </Typography>
        <Typography sx={{ backgroundColor: 'grey.100' }}>
          {InvoiceShowFormattedMessages.yourRef}: {invoice?.your_ref || '-'}
        </Typography>
        <Typography>
          {InvoiceShowFormattedMessages.invoiceNo}: {invoice?.invoice_no}
        </Typography>
      </Box>
    );
  };

  /*
    Generate the Currency Type for the Invoice
  */
  const generateInvoiceCurrency = () => {
    const currency = getCurrencyType(invoice);

    return (
      <Box sx={{ display: 'flex', justifyContent: 'end', marginY: 2 }}>
        <Typography>
          <strong>
            {currency} {getCurrencySymbol(currency)}
          </strong>
        </Typography>
      </Box>
    );
  };

  const generateInvoiceBillPeriods = () => {
    const { startDate, endDate } = getBillDates(invoice);

    if (startDate === endDate) {
      return (
        <Typography fontWeight={700} gutterBottom>
          {InvoiceShowFormattedMessages.prepaidBillDate} {startDate}
        </Typography>
      );
    }

    return (
      <Typography fontWeight={700} gutterBottom>
        {InvoiceShowFormattedMessages.licenseAndMessagesFor} {startDate}{' '}
        {InvoiceShowFormattedMessages.to} {endDate}
      </Typography>
    );
  };

  const generateSoftwareLicenseFees = () => {
    let html = null;

    if (invoice?.software_license_fees?.length !== 0) {
      html = (invoice?.software_license_fees || []).map((fee) => {
        return (
          <TableRow key={invoice?.software_license_fees?.indexOf(fee)}>
            <TableCell sx={{ paddingLeft: 4 }}>
              <Typography>{fee.translation || fee.revenue_type}</Typography>
            </TableCell>
            <TableCell></TableCell>
            <TableCell>
              <Typography>{fee.quantity}</Typography>
            </TableCell>
            <TableCell>@</TableCell>
            <TableCell>
              <Typography>
                {formatCurrency(fee.rate, getCurrencyType(invoice), 4)}
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ width: '150px' }}>
              <Typography>
                <strong>{fee.revenue}</strong>
              </Typography>
            </TableCell>
          </TableRow>
        );
      });

      return (
        <Fragment>
          <TableRow>
            <TableCell>
              <Typography>
                {InvoiceShowFormattedMessages.softwareLicenseFees}
              </Typography>
            </TableCell>
          </TableRow>
          {html}
        </Fragment>
      );
    }
    return null;
  };

  const generateSetupAndTxtMeFees = () => {
    if (!invoice?.setup_and_textme_fees) return null;

    return (
      <Fragment>
        {(invoice.setup_and_textme_fees || []).map((fee) => (
          <TableRow key={invoice.setup_and_textme_fees.indexOf(fee)}>
            <TableCell width="40%" sx={{ paddingLeft: 4 }}>
              <Typography>{fee.translation || fee.revenue_type}</Typography>
            </TableCell>
            <TableCell></TableCell>
            <TableCell>
              <Typography>{fee.quantity}</Typography>
            </TableCell>
            <TableCell>@</TableCell>
            <TableCell>
              <Typography>
                {formatCurrency(fee.rate, getCurrencyType(invoice))}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography>
                <strong>{fee.revenue}</strong>
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </Fragment>
    );
  };

  const generatePlanFees = () => {
    if (!invoice?.red_oxygen_plan_fees) return null;

    return (invoice.red_oxygen_plan_fees || []).map((fee) => {
      return (
        <TableRow key={invoice.red_oxygen_plan_fees.indexOf(fee)}>
          <TableCell sx={{ paddingLeft: 4 }}>
            <Typography>{fee.translation || fee.revenue_type}</Typography>
          </TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell align="right" sx={{ width: '150px' }}>
            <Typography>
              <strong>{fee.revenue}</strong>
            </Typography>
          </TableCell>
        </TableRow>
      );
    });
  };

  const generateMessageFees = () => {
    let html = null;

    let planFees = generatePlanFees();

    if (
      invoice &&
      ((invoice.text_message_fees && invoice.text_message_fees.length !== 0) ||
        planFees)
    ) {
      html = invoice.text_message_fees.map((fee) => {
        return (
          <TableRow key={invoice.text_message_fees.indexOf(fee)}>
            <TableCell sx={{ paddingLeft: 4 }}>
              <Typography>{fee.translation || fee.revenue_type}</Typography>
            </TableCell>
            <TableCell></TableCell>
            <TableCell>
              <Typography>{fee.quantity}</Typography>
            </TableCell>
            <TableCell>@</TableCell>
            <TableCell>
              <Typography>
                {formatCurrency(fee.rate, getCurrencyType(invoice), 4)}
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ width: '150px' }}>
              <Typography>
                <strong>{fee.revenue}</strong>
              </Typography>
            </TableCell>
          </TableRow>
        );
      });

      return (
        <Fragment>
          <TableRow>
            <TableCell>
              <Typography>
                {InvoiceShowFormattedMessages.messageFees}
              </Typography>
            </TableCell>
          </TableRow>
          {planFees}
          {html}
        </Fragment>
      );
    }
    return null;
  };

  const generateRebates = () => {
    let html = null;
    if (invoice && invoice.rebates && invoice.rebates.length !== 0) {
      html = invoice.rebates.map((fee) => {
        return (
          <TableRow key={invoice.rebates.indexOf(fee)}>
            <TableCell sx={{ paddingLeft: 4 }}>
              <Typography>{fee.translation || fee.revenue_type}</Typography>
            </TableCell>
            <TableCell></TableCell>
            <TableCell>
              <Typography>{fee.quantity}</Typography>
            </TableCell>
            <TableCell>@</TableCell>
            <TableCell>
              <Typography>
                {formatCurrency(fee.rate, getCurrencyType(invoice), 4)}
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ width: '150px' }}>
              <Typography>
                <strong>{fee.revenue}</strong>
              </Typography>
            </TableCell>
          </TableRow>
        );
      });
      return html;
    }
  };

  const generateBillAdjustments = () => {
    if (!invoice?.bill_adjustments) return null;

    return (invoice.bill_adjustments || []).map((fee) => (
      <TableRow key={invoice.bill_adjustments.indexOf(fee)}>
        <TableCell>
          <Typography>
            {fee.translation || fee.revenue_type} - {fee.bill_desc}
          </Typography>
        </TableCell>
        <TableCell></TableCell>
        <TableCell>
          <Typography>{fee.quantity}</Typography>
        </TableCell>
        <TableCell>@</TableCell>
        <TableCell>
          <Typography>
            {formatCurrency(fee.rate, getCurrencyType(invoice), 4)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography>
            <strong>{fee.revenue}</strong>
          </Typography>
        </TableCell>
      </TableRow>
    ));
  };

  const generateTotalCharges = () => {
    const { credit_card_surcharge, country } = invoice;
    const { inc_gst, exc_gst, gst } = invoice.bill_totals || {};

    let creditCardCharge;
    if (credit_card_surcharge)
      creditCardCharge = (
        <TableRow>
          <TableCell width="40%">
            <Typography>
              {InvoiceShowFormattedMessages.creditCardSurcharge}
            </Typography>
          </TableCell>
          <TableCell colSpan="4"></TableCell>
          <TableCell colSpan="1" align="right">
            <Typography>{credit_card_surcharge.revenue}</Typography>
          </TableCell>
        </TableRow>
      );

    return (
      <Fragment>
        <TableRow>
          <TableCell width="40%">
            <Typography>
              {InvoiceShowFormattedMessages.totalChargesExcludingTax}
            </Typography>
          </TableCell>
          <TableCell colSpan="4"></TableCell>
          <TableCell
            colSpan="1"
            align="right"
            sx={{ borderTop: '1.5px solid black' }}
          >
            <Typography>{exc_gst}</Typography>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell width="40%">
            <Typography>
              {country === 'Australia'
                ? InvoiceShowFormattedMessages.gst
                : InvoiceShowFormattedMessages.tax}
            </Typography>
          </TableCell>
          <TableCell colSpan="4"></TableCell>
          <TableCell colSpan="1" align="right">
            <Typography>{gst}</Typography>
          </TableCell>
        </TableRow>
        {creditCardCharge}
        <TableRow>
          <TableCell width="40%">
            <Typography>
              <strong>{InvoiceShowFormattedMessages.totalCharges}</strong>
            </Typography>
          </TableCell>
          <TableCell colSpan="4"></TableCell>
          <TableCell
            colSpan="1"
            align="right"
            sx={{
              borderTop: '2px solid black',
              borderBottom: '2px solid black',
              fontWeight: 700,
            }}
          >
            <Typography>
              <strong>
                {formatCurrency(inc_gst, getCurrencyType(invoice))}
              </strong>
            </Typography>
          </TableCell>
        </TableRow>
      </Fragment>
    );
  };

  const generatePayment = () => {
    const { successfulPayment, failedPayments } = invoice;

    let html = [];

    if (successfulPayment) {
      const { paid_date, paid_amount, cc_name, cc_number_suffix } =
        successfulPayment;

      const paymentText = (
        <Typography>
          <strong>
            {paid_date} {'(UTC)'} {InvoiceShowFormattedMessages.youPaid}{' '}
            {paid_amount}
          </strong>
        </Typography>
      );
      html.push(
        <Fragment key="success">
          {paymentText}
          <br />
          {InvoiceShowFormattedMessages.nameOnCC}: {cc_name} <br />
          {InvoiceShowFormattedMessages.cCLastFour}: xxxxxxxxxxxx
          {cc_number_suffix} <br />
        </Fragment>
      );
    } else {
      html.push(
        <Fragment key="due">
          <Typography>
            {InvoiceShowFormattedMessages.paymentDueBy}{' '}
            {invoice.bill_dates?.due_date}
          </Typography>
          <br />
        </Fragment>
      );
    }

    if (failedPayments && failedPayments.length > 0) {
      for (const failedPayment of failedPayments) {
        const {
          paid_date,
          response_text,
          cc_name,
          cc_number_suffix,
          payment_id,
        } = failedPayment;
        html.push(
          <Fragment key={payment_id}>
            <br />
            <Typography sx={{ color: 'error.main' }}>
              <strong>
                {paid_date} {'(UTC)'} {'Payment failed'}
              </strong>
            </Typography>
            <br />
            {InvoiceShowFormattedMessages.nameOnCC}: {cc_name} <br />
            {InvoiceShowFormattedMessages.cCLastFour}: xxxxxxxxxxxx
            {cc_number_suffix} <br />
            Error: <i>{response_text}</i>
            <br />
          </Fragment>
        );
      }
    }

    return <Box sx={{ textAlign: 'right' }}>{html}</Box>;
  };

  const generateBankDetails = () => {
    const bankAddress = invoice?.bankAddress || '';

    return (
      <Typography
        dangerouslySetInnerHTML={{ __html: bankAddress }}
      ></Typography>
    );
  };

  return (
    <Fragment>
      {invoice && (
        <Box>
          {generateInvoiceHeader()}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {generateCustomerHeader()}
            {generateInvoiceDates()}
          </Box>
          {generateInvoiceCurrency()}
          {generateInvoiceBillPeriods()}
          <TableContainer sx={{ marginBottom: 4 }}>
            <Table width="100%">
              <TableBody>
                {generateSoftwareLicenseFees()}
                {generateSetupAndTxtMeFees()}
                {generateMessageFees()}
                {generateRebates()}
                {generateBillAdjustments()}
                {generateTotalCharges()}
              </TableBody>
            </Table>
          </TableContainer>
          {generatePayment()}
          {generateBankDetails()}
        </Box>
      )}
    </Fragment>
  );
};

export default SingleInvoice;
